import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';

export default function LojistaDashboardPage() {
  const { user } = useAuthStore();
  const [carriers, setCarriers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/freight/carriers/enabled').catch(() => ({ data: { data: { carriers: [] } } })),
      api.get('/freight/tokens').catch(() => ({ data: { data: { tokens: [] } } })),
    ]).then(([carriersRes, tokensRes]) => {
      setCarriers(carriersRes.data.data?.carriers || carriersRes.data.data || []);
      setTokens(tokensRes.data.data?.tokens || []);
    }).finally(() => setLoading(false));
  }, []);

  const tokensAtivos = tokens.filter((t) => t.is_active).length;

  const cards = [
    { key: 'carriers', label: 'Transportadoras Ativas', icon: '🚚', color: '#1e40af', value: carriers.length },
    { key: 'tokens', label: 'Tokens de API Ativos', icon: '🔑', color: '#7c3aed', value: tokensAtivos },
  ];

  return (
    <div>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Olá, {user?.name?.split(' ')[0]} 👋</h1>
        <p style={styles.pageDesc}>Visão geral da sua integração com o BringHub Frete</p>
      </div>

      <div style={styles.statsGrid}>
        {cards.map((card) => (
          <div key={card.key} style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: `${card.color}15`, color: card.color }}>
              {card.icon}
            </div>
            <div>
              <div style={styles.statValue}>{loading ? '—' : card.value}</div>
              <div style={styles.statLabel}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.infoGrid}>
        <div style={styles.infoCard}>
          <h2 style={styles.infoTitle}>🚚 Transportadoras Habilitadas</h2>
          {loading ? (
            <p style={styles.loading}>Carregando...</p>
          ) : carriers.length === 0 ? (
            <div style={styles.emptyBox}>
              <p style={styles.emptyText}>Nenhuma transportadora habilitada ainda.</p>
              <Link to="/admin/freight" style={styles.linkBtn}>Configurar transportadoras →</Link>
            </div>
          ) : (
            <div style={styles.carrierList}>
              {carriers.map((c) => (
                <div key={c.slug} style={styles.carrierRow}>
                  <span style={styles.carrierDot} />
                  <span style={styles.carrierName}>{c.name}</span>
                  <span style={styles.carrierBadge}>{c.use_own_credentials ? 'Chaves próprias' : 'Chaves do sistema'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.infoCard}>
          <h2 style={styles.infoTitle}>⚡ Atalhos</h2>
          <div style={styles.shortcutList}>
            {user?.permissions?.simulador_fretes !== false && (
              <Link to="/admin/correios" style={styles.shortcutItem}>
                <span>🧪</span> Simular Frete
              </Link>
            )}
            {user?.permissions?.fretes !== false && (
              <Link to="/admin/freight" style={styles.shortcutItem}>
                <span>🚚</span> Gerenciar Transportadoras
              </Link>
            )}
            {user?.permissions?.tokens_api !== false && (
              <Link to="/admin/tokens" style={styles.shortcutItem}>
                <span>🔑</span> Gerenciar Tokens de API
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageHeader: { marginBottom: '28px' },
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  pageDesc: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: '#ffffff', borderRadius: '12px', padding: '20px',
    display: 'flex', alignItems: 'center', gap: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
  },
  statIcon: {
    width: '48px', height: '48px', borderRadius: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '22px', flexShrink: 0,
  },
  statValue: { fontSize: '26px', fontWeight: '700', color: '#111827', lineHeight: 1.2 },
  statLabel: { fontSize: '13px', color: '#6b7280', marginTop: '2px' },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
  },
  infoCard: {
    background: '#ffffff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
  },
  infoTitle: { fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px' },
  loading: { color: '#9ca3af', fontSize: '14px' },
  emptyBox: { textAlign: 'center', padding: '20px 0' },
  emptyText: { fontSize: '13px', color: '#9ca3af', marginBottom: '10px' },
  linkBtn: { fontSize: '13px', color: '#2563eb', fontWeight: '600', textDecoration: 'none' },
  carrierList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  carrierRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  carrierDot: { width: '8px', height: '8px', borderRadius: '50%', background: '#16a34a', flexShrink: 0 },
  carrierName: { flex: 1, fontSize: '14px', color: '#374151', fontWeight: '500' },
  carrierBadge: { fontSize: '11px', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '10px' },
  shortcutList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  shortcutItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 12px', borderRadius: '8px', background: '#f9fafb',
    color: '#374151', fontSize: '14px', fontWeight: '500',
    textDecoration: 'none', border: '1px solid #f3f4f6',
  },
};
